/**
 * Geofence layer (ENG-315) — FlightBlender restricted areas on the Drones and
 * OI maps. Red outline + diagonal red hatching, so a geofence reads differently
 * from an OI volume (and from a non-conforming OI, which is a dashed red line
 * with a plain fill).
 *
 * The API is per region, so geofences are only asked for once the map is zoomed
 * in far enough to be looking at a region, and re-asked when the view moves.
 * One file per layer (see CLAUDE.md §3).
 */
import { type Geofence, fetchGeofences, isActiveGeofence } from '@/lib/api/geofences';
import type { OiBbox } from '@/lib/api/oiSearch';
import { VOLUME_PALETTE } from '@/lib/map/volumeStyle';
import { queryClient } from '@/lib/query';
import { type Context, useUiStore } from '@/lib/store/ui';
import type { Feature, Polygon } from 'geojson';
import mapboxgl from 'mapbox-gl';
import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';

const SRC = 'src-geofence';
const FILL_LAYER = 'lyr-geofence-fill';
const LINE_LAYER = 'lyr-geofence-line';
const HATCH = 'geofence-hatch';

/** Below this the map is showing a province, not a region — draw nothing and ask
 *  for nothing. */
export const GEOFENCE_MIN_ZOOM = 9;

/** The two maps that carry geofences. */
const CONTEXTS: readonly Context[] = ['drones', 'oi'];

const SNAP_DEG = 0.25;
const PAD = 0.2;
const REFRESH_MS = 60_000;

export function shouldShowGeofences(input: {
  ctx: Context;
  enabled: boolean;
  zoom: number;
}): boolean {
  return input.enabled && CONTEXTS.includes(input.ctx) && input.zoom >= GEOFENCE_MIN_ZOOM;
}

/** The visible bounds, padded by 20 % and snapped outward to a 0.25° grid, so a
 *  small pan doesn't change the request (and the cache key) every time. */
export function snapBounds(b: OiBbox): OiBbox {
  const dLat = (b.neLat - b.swLat) * PAD;
  const dLng = (b.neLng - b.swLng) * PAD;
  const snapDown = (v: number) => Math.floor(v / SNAP_DEG) * SNAP_DEG;
  const snapUp = (v: number) => Math.ceil(v / SNAP_DEG) * SNAP_DEG;
  return {
    swLat: Math.max(-90, snapDown(b.swLat - dLat)),
    swLng: Math.max(-180, snapDown(b.swLng - dLng)),
    neLat: Math.min(90, snapUp(b.neLat + dLat)),
    neLng: Math.min(180, snapUp(b.neLng + dLng)),
  };
}

export const boundsKey = (b: OiBbox): string => `${b.swLat},${b.swLng},${b.neLat},${b.neLng}`;

/** 16×16 transparent tile with a faint red wash and 45° red stripes — a fill
 *  pattern (Mapbox can't hatch a plain `fill-color`). */
function hatchImage(): { width: number; height: number; data: Uint8Array } {
  const size = 16;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const stripe = (x + y) % size < 3;
      data[i] = 196; // #c41a1a
      data[i + 1] = 26;
      data[i + 2] = 26;
      data[i + 3] = stripe ? 120 : 14;
    }
  }
  return { width: size, height: size, data };
}

export function ensureGeofenceLayer(map: MapboxMap) {
  if (!map.hasImage(HATCH)) map.addImage(HATCH, hatchImage(), { pixelRatio: 2 });
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  // Under the OI volumes, so a flight area stays readable on top of a geofence.
  const above = map.getLayer('lyr-oi-fill') ? 'lyr-oi-fill' : undefined;
  if (!map.getLayer(FILL_LAYER)) {
    map.addLayer(
      { id: FILL_LAYER, type: 'fill', source: SRC, paint: { 'fill-pattern': HATCH } },
      above,
    );
  }
  if (!map.getLayer(LINE_LAYER)) {
    map.addLayer(
      {
        id: LINE_LAYER,
        type: 'line',
        source: SRC,
        paint: { 'line-color': VOLUME_PALETTE.red, 'line-width': 1.5, 'line-opacity': 0.9 },
      },
      above,
    );
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

const day = (iso: string): string => iso.slice(0, 10);

function altitudeText(lower: unknown, upper: unknown, ref: unknown): string {
  const fmt = (v: unknown) => (typeof v === 'number' ? `${Math.round(v)} m` : '—');
  return `${fmt(lower)} – ${fmt(upper)} ${escapeHtml(String(ref ?? ''))}`.trim();
}

function buildPopup(p: Record<string, unknown>): string {
  const name = escapeHtml(String(p.name ?? 'Geofence'));
  const alt = altitudeText(p.lower_m, p.upper_m, p.altitude_ref);
  const from = escapeHtml(day(String(p.start_utc ?? '')));
  const to = escapeHtml(day(String(p.end_utc ?? '')));
  return `<div style="font-family:Inter,sans-serif;min-width:160px">
    <div style="font-size:0.615em;font-weight:700;letter-spacing:.06em;color:${VOLUME_PALETTE.red};margin-bottom:2px">GEOFENCE</div>
    <div style="font-weight:700;font-size:0.846em;margin-bottom:4px">${name}</div>
    <div style="font-size:0.769em"><b>Altitude:</b> ${alt}</div>
    <div style="font-size:0.769em"><b>Active:</b> ${from} → ${to}</div>
  </div>`;
}

export function subscribeGeofenceLayer(map: MapboxMap): () => void {
  ensureGeofenceLayer(map);

  let current: Geofence[] = [];
  let loadedKey: string | null = null;
  let loadSeq = 0;
  let debounce: number | null = null;

  const draw = () => {
    const src = map.getSource(SRC) as GeoJSONSource | undefined;
    if (!src) return;
    const ui = useUiStore.getState();
    const show = shouldShowGeofences({
      ctx: ui.activeContext,
      enabled: ui.showGeofences,
      zoom: map.getZoom(),
    });
    const now = Date.now();
    const features: Feature<Polygon>[] = show
      ? current
          .filter((g) => isActiveGeofence(g, now))
          .map((g) => ({
            type: 'Feature',
            geometry: g.polygon,
            properties: {
              id: g.id,
              name: g.name,
              lower_m: g.lower_m,
              upper_m: g.upper_m,
              altitude_ref: g.altitude_ref,
              start_utc: g.start_utc,
              end_utc: g.end_utc,
            },
          }))
      : [];
    src.setData({ type: 'FeatureCollection', features });
  };

  const load = () => {
    const ui = useUiStore.getState();
    const show = shouldShowGeofences({
      ctx: ui.activeContext,
      enabled: ui.showGeofences,
      zoom: map.getZoom(),
    });
    if (!show) {
      draw();
      return;
    }
    const b = map.getBounds();
    if (!b) return;
    const bbox = snapBounds({
      swLat: b.getSouth(),
      swLng: b.getWest(),
      neLat: b.getNorth(),
      neLng: b.getEast(),
    });
    const key = boundsKey(bbox);
    if (key === loadedKey) {
      draw();
      return;
    }
    loadedKey = key;
    const seq = ++loadSeq;
    queryClient
      .fetchQuery({
        queryKey: ['geofences', key],
        queryFn: () => fetchGeofences(bbox),
        staleTime: REFRESH_MS,
      })
      .then((list) => {
        if (seq !== loadSeq) return; // the view moved on while this was in flight
        current = list;
        draw();
      })
      .catch(() => {
        // Keep what's drawn and retry on the next move or refresh tick.
        if (seq === loadSeq) loadedKey = null;
      });
  };

  const schedule = () => {
    if (debounce !== null) window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      debounce = null;
      load();
    }, 250);
  };

  load();
  map.on('moveend', schedule);
  const unsubUi = useUiStore.subscribe((s) => `${s.activeContext}|${s.showGeofences}`, schedule);
  // Re-ask once a minute (the cache honours its own freshness) and let expired
  // geofences drop out of the drawing.
  const refresh = window.setInterval(() => {
    loadedKey = null;
    load();
  }, REFRESH_MS);

  const popup = new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '240px',
    offset: 12,
  });
  const onClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
    // An OI volume drawn over the geofence owns the click (its own popup).
    if (
      map.getLayer('lyr-oi-fill') &&
      map.queryRenderedFeatures(e.point, { layers: ['lyr-oi-fill'] }).length > 0
    ) {
      return;
    }
    const f = e.features?.[0];
    if (!f) return;
    popup
      .setLngLat(e.lngLat)
      .setHTML(buildPopup((f.properties ?? {}) as Record<string, unknown>))
      .addTo(map);
  };
  const onEnter = () => {
    map.getCanvas().style.cursor = 'pointer';
  };
  const onLeave = () => {
    map.getCanvas().style.cursor = '';
  };
  map.on('click', FILL_LAYER, onClick);
  map.on('mouseenter', FILL_LAYER, onEnter);
  map.on('mouseleave', FILL_LAYER, onLeave);

  return () => {
    if (debounce !== null) window.clearTimeout(debounce);
    window.clearInterval(refresh);
    unsubUi();
    popup.remove();
    map.off('moveend', schedule);
    map.off('click', FILL_LAYER, onClick);
    map.off('mouseenter', FILL_LAYER, onEnter);
    map.off('mouseleave', FILL_LAYER, onLeave);
  };
}
